import "./TaskImages.scss";
import { useState, useRef, useEffect } from "react";
import { fetcher } from "../../utils/fetcher";
import ToolTip from "../ToolTip/ToolTip";
import Spinner from "../Spinner/Spinner";
import { useTranslation } from "react-i18next";

function TaskImages({ taskData, onTaskUpdate }) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const fileInputRef = useRef(null);
  const toolTipRef = useRef();

  const showToolTip = (e, arrow, content) => {
    const triggerElement = e.currentTarget;
    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  // Handle file upload
  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      alert(t("tasks.images.invalidFile"));
      return;
    }

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert(t("tasks.images.tooLarge"));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetcher.post(
        `/api/tasks/${taskData._id}/upload-image`,
        formData,
      );

      if (response.success) {
        // Update task data with new image
        const updatedTask = {
          ...taskData,
          images: [...(taskData.images || []), response.image],
        };

        if (onTaskUpdate) {
          onTaskUpdate(updatedTask);
        }
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert(t("tasks.images.uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  };

  // Handle image deletion
  const handleImageDelete = async (imageId, e) => {
    // Stop event propagation to prevent opening the image viewer
    e.stopPropagation();

    if (!confirm(t("tasks.images.confirmDelete"))) {
      return;
    }

    setDeletingImageId(imageId);

    try {
      const response = await fetcher.delete(
        `/api/tasks/${taskData._id}/images/${imageId}`,
      );

      if (response.success) {
        // Update task data by removing the deleted image
        const updatedTask = {
          ...taskData,
          images: taskData.images.filter((img) => img.id !== imageId),
        };

        if (onTaskUpdate) {
          onTaskUpdate(updatedTask);
        }
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      alert(t("tasks.images.deleteFailed"));
    } finally {
      setDeletingImageId(null);
    }
  };

  // Handle button click to trigger file input
  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input value to allow uploading the same file again
    e.target.value = "";
  };

  // Handle paste event
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          await handleImageUpload(file);
        }
        break;
      }
    }
  };

  // Open image viewer modal
  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  // Navigate to previous image
  const handlePreviousImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  // Navigate to next image
  const handleNextImage = () => {
    if (selectedImageIndex < (taskData.images?.length || 0) - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      handlePreviousImage();
    } else if (e.key === "ArrowRight") {
      handleNextImage();
    } else if (e.key === "Escape") {
      setSelectedImageIndex(null);
    }
  };

  // Close image viewer
  const handleCloseImageViewer = () => {
    setSelectedImageIndex(null);
  };

  // Auto-focus the overlay when image viewer opens for keyboard navigation
  useEffect(() => {
    if (selectedImageIndex !== null) {
      // Small delay to ensure the overlay is rendered
      setTimeout(() => {
        document.querySelector(".image-viewer-overlay")?.focus();
      }, 10);
    }
  }, [selectedImageIndex]);

  if (!taskData) {
    return (
      <div className="task-images">
        <div className="task-images-wrapper">
          <div className="placeholder">{t("tasks.images.noTaskSelected")}</div>
        </div>
      </div>
    );
  }

  const images = taskData.images || [];

  return (
    <div className="task-images" onPaste={handlePaste} tabIndex={0}>
      <ToolTip ref={toolTipRef} />

      {/* Upload Area */}
      <div className="upload-section">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />

        <div className="upload-area">
          <div className="upload-icon-wrapper">
            <span className="material-symbols-outlined upload-icon">
              cloud_upload
            </span>
          </div>

          <div className="upload-text">
            <p className="upload-title">{t("tasks.images.addImages")}</p>
            <p className="upload-description">
              {t("tasks.images.uploadDescription")}
            </p>
          </div>

          <button
            className="upload-button"
            onClick={handleUploadButtonClick}
            disabled={isUploading}
            onMouseEnter={(e) =>
              showToolTip(e, "top-center", t("tasks.images.uploadImage"))
            }
            onMouseLeave={() => toolTipRef.current?.close()}
          >
            <span className="material-symbols-outlined">
              {isUploading ? "sync" : "add"}
            </span>
            {isUploading
              ? t("tasks.images.uploading")
              : t("tasks.images.chooseFile")}
          </button>
        </div>

        <div className="upload-hint">
          <span className="material-symbols-outlined hint-icon">info</span>
          {t("tasks.images.supported")}
        </div>
      </div>

      {/* Divider */}
      {images.length > 0 && (
        <div className="section-divider">
          <span className="divider-text">
            {t("tasks.images.uploadedCount", { count: images.length })}
          </span>
        </div>
      )}

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="images-grid">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="image-thumbnail"
              onClick={() => handleImageClick(index)}
              onMouseEnter={(e) => showToolTip(e, "top-center", image.filename)}
              onMouseLeave={() => toolTipRef.current?.close()}
            >
              <img src={image.thumbnailUrl} alt={image.filename} />
              <button
                className="delete-image-btn"
                onClick={(e) => handleImageDelete(image.id, e)}
                disabled={deletingImageId === image.id}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  showToolTip(e, "top-center", t("tasks.images.deleteImage"));
                }}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                {deletingImageId === image.id ? (
                  <span className="material-symbols-outlined spinning">
                    sync
                  </span>
                ) : (
                  <span className="material-symbols-outlined">close</span>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && !isUploading && (
        <div className="no-images">{t("tasks.images.empty")}</div>
      )}

      {/* Full-size Image Viewer Modal */}
      {selectedImageIndex !== null && (
        <div
          className="image-viewer-overlay"
          onClick={handleCloseImageViewer}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <div
            className="image-viewer-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="close-button"
              onClick={handleCloseImageViewer}
              onMouseEnter={(e) =>
                showToolTip(e, "top-center", t("tasks.images.closeEsc"))
              }
              onMouseLeave={() => toolTipRef.current?.close()}
            >
              <span className="material-symbols-outlined">close</span>
            </button>

            {/* Previous button */}
            {selectedImageIndex > 0 && (
              <button
                className="nav-button prev-button"
                onClick={handlePreviousImage}
                onMouseEnter={(e) =>
                  showToolTip(e, "top-center", t("tasks.images.previous"))
                }
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            )}

            {/* Image */}
            <div className="image-container">
              <img
                src={images[selectedImageIndex].originalUrl}
                alt={images[selectedImageIndex].filename}
              />
            </div>

            {/* Next button */}
            {selectedImageIndex < images.length - 1 && (
              <button
                className="nav-button next-button"
                onClick={handleNextImage}
                onMouseEnter={(e) =>
                  showToolTip(e, "top-center", t("tasks.images.next"))
                }
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            )}

            {/* Image info */}
            <div className="image-info">
              <div className="image-filename">
                {images[selectedImageIndex].filename}
              </div>
              <div className="image-counter">
                {selectedImageIndex + 1} / {images.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskImages;
